'use client';

import { useMemo, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/react/shallow';
import { FileImage } from 'lucide-react';
import { EmptyState } from '@/shared/ui';
import { useJobQueueStore, type AlbumJob } from '@/features/design/job-queue';
import { useAlbum } from '../hooks/use-album';
import { AlbumsList } from './AlbumsList';
import type { Album } from '../api/album-service';

interface AlbumListWithViewerProps {
  conceptId: string;
  projectId: string;
  conceptTitle?: string | null;
  conceptNumber?: number;
  coverImageUrl?: string | null;
}

/**
 * Two-column right pane: just the list of albums with cover thumbnails.
 *
 * Apple parity: shares `AlbumListRow` (via `AlbumsList`) with the single-column
 * `AlbumCollapsiblePanel`, so the cover, title, format/language line, date,
 * and action icons are identical. The previous incarnation rendered the
 * cover-less `AlbumItem` plus an inline iframe PDF preview; Apple has no such
 * preview (it presents the PDF fullscreen via `.albumPDFViewer`), so the
 * iframe was removed for cross-platform parity. Clicking an album's link
 * action still opens the public URL — the row's tap targets are the action
 * icons themselves.
 */
export function AlbumListWithViewer({
  conceptId,
  conceptTitle,
  conceptNumber,
  coverImageUrl,
  projectId,
}: AlbumListWithViewerProps) {
  const t = useTranslations('album');
  const scrollRef = useRef<HTMLDivElement>(null);

  const { albums, deleteAlbum, isDeleting, downloadAlbum } = useAlbum({
    conceptId,
  });

  // Subscribe to in-flight album jobs so generating / pending rows appear
  // immediately, matching `AlbumCollapsiblePanel`'s merge behaviour.
  const albumJobsFromQueue = useJobQueueStore(
    useShallow((state) => state.getAllAlbumsForConcept?.(conceptId) || [])
  );

  // Merge mirrors `useAlbumPanel.mergedAlbums`: API album is the source of
  // truth for `album_title`, `file_size`, `page_count`, etc.; the job queue is
  // the source of truth for in-flight status/progress. When both exist for
  // the same id and the API row is completed, prefer it — otherwise the cover
  // loses its LLM-generated title (Apple parity bug seen on web two-column).
  const mergedAlbums = useMemo<Array<Album | AlbumJob>>(() => {
    const albumsById = new Map<string, Album>();
    albums.forEach((album) => albumsById.set(album.id, album));

    const result: Array<Album | AlbumJob> = [];
    const seenIds = new Set<string>();

    albumJobsFromQueue.forEach((job) => {
      const apiAlbum = albumsById.get(job.id);
      if (apiAlbum && apiAlbum.status === 'completed') {
        result.push(apiAlbum);
      } else {
        result.push(job);
      }
      seenIds.add(job.id);
    });

    albums.forEach((album) => {
      if (!seenIds.has(album.id)) {
        result.push(album);
      }
    });

    return result.sort((a, b) => {
      const dateA = 'createdAt' in a ? a.createdAt : a.created_at;
      const dateB = 'createdAt' in b ? b.createdAt : b.created_at;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [albumJobsFromQueue, albums]);

  // Scroll the list to top whenever a fresh album lands at the front —
  // mirrors `AlbumGeneratorView.twoColumnContent`'s `ScrollViewReader`.
  const firstAlbumId = mergedAlbums[0]?.id;
  useEffect(() => {
    if (scrollRef.current && firstAlbumId) {
      scrollRef.current.scrollTop = 0;
    }
  }, [firstAlbumId]);

  if (mergedAlbums.length === 0) {
    return (
      <EmptyState
        icon={<FileImage />}
        title={t('noAlbums')}
        description={t.rich('noAlbumsHint', {
          link: (chunks) => (
            <Link
              href={`/project/${projectId}/concepts`}
              className="text-primary underline-offset-4 hover:underline"
            >
              {chunks}
            </Link>
          ),
        })}
        className="py-12"
      />
    );
  }

  // `px-4 pb-6` gives the album cover's box-shadow room before the scroll
  // container's `overflow-y-auto` (which forces `overflow-x` to clip on most
  // engines too) chops the soft diffuse layer at the right/bottom edge —
  // same trap that `ProjectAlbumsSection`'s `pb-7` was added to fix on the
  // dashboard carousel.
  return (
    <div ref={scrollRef} className="h-full overflow-y-auto px-4 pb-6">
      <AlbumsList
        albums={mergedAlbums}
        highlightedAlbumId={null}
        isDeleting={isDeleting}
        onDelete={deleteAlbum}
        onDownload={downloadAlbum}
        conceptTitle={conceptTitle}
        conceptNumber={conceptNumber}
        coverImageUrl={coverImageUrl}
        twoUp
      />
    </div>
  );
}
